import { RadioGroup } from '@headlessui/react';
import { useTour } from '@reactour/tour';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWizard } from 'react-use-wizard';
import { useTheme } from 'styled-components';

import WaterDropOffSVG from '@/assets/icons/water-drop-off.svg';
import WaterDropOnSVG from '@/assets/icons/water-drop-on.svg';
import { ErrorAlert } from '@/components/common/error-alert';
import { SpinningIcon } from '@/components/common/spinning-icon';
import { SignedInMethod, useAuth } from '@/features/auth';
import { getTourStep } from '@/features/demo/steps';
import { addBatchToFermentation } from '@/features/fermentation/services/fermentations';
import { createBatch } from '@/features/single-measurement/services/batches';
import {
  BioEthMethods,
  IBatch,
  ICreateBatch,
  Method,
  WineMethods,
  YeastMethods,
} from '@/features/single-measurement/types';
import { useSettings } from '@/features/user-settings';

import { IStep } from '../types';

export const MethodSelectionStep: React.FC<IStep & { fermentationId?: string }> = ({
  setState,
  fermentationId,
}) => {
  const { user } = useAuth();
  const theme: any = useTheme();
  const { t } = useTranslation();
  const { settings } = useSettings();
  const { nextStep, handleStep } = useWizard();

  const [method, setMethod] = useState<Method | null>(null);
  const { isOpen: isTourOpen, setCurrentStep, steps } = useTour();

  const [errorMessage, setErrorMessage] = useState<JSX.Element | string>('');

  // if in demo mode open the tour
  useEffect(() => {
    if (isTourOpen) setCurrentStep(getTourStep(steps, 'RLqQcrK')); // #step-new-viab-conz-create
  }, []);

  // get analysis methods based on the signed method
  const { conz, conzViab } = useMemo(
    () => getAnalysisMethods(user?.signedInMethod),
    [user?.signedInMethod],
  );

  const { mutateAsync, isLoading } = useMutation({
    networkMode: 'always',
    mutationFn: () => {
      const body: ICreateBatch = {
        method: method!,
        appVersion: 'web_2.0.0',
        methodAttributes: { stain: settings.stain, analysisLevel: settings.cellSize },
      };

      // add budding sensitivity if method is ethanol
      if (BioEthMethods.includes(method!)) {
        body['tags'] = { budding_sensitivity: settings.buddingSensitivity };
      }

      // add elongated cell if method is BB and enabled in settings
      if (YeastMethods.includes(method!) && settings.elongatedCell) {
        body['tags'] = { long_cells: true };
      }

      // add lactobacillus detection if enabled in settings | methods are FW and BB
      if (
        [...YeastMethods, ...WineMethods].includes(method!) &&
        settings.lactobacillusDetection
      ) {
        body['tags'] = { detect_lact: true };
      }

      return fermentationId
        ? addBatchToFermentation(fermentationId, {
            ...body,
            locallyCreatedAt: new Date(),
          })
        : createBatch(body);
    },

    onSuccess: ({ id: batchId }: IBatch) => {
      setState((prevState) => ({ ...prevState, batchId }));
    },

    onError(error: any) {
      if (error?.response?.status === 402) {
        setErrorMessage(
          <p>
            Your account is currently suspended, no new measurements are possible. If you
            wish to reactivate the account please get in touch via{' '}
            <a href="mailto:support@oculyze.net" className="text-blue-700 underline">
              success@oculyze.net
            </a>
          </p>,
        );
        return;
      }
      setErrorMessage(String(error));
    },
  });

  // info: wizard library has inbuilt support for react query
  // once the promise resolved, wizard will be moved to the next step
  handleStep(() => mutateAsync());

  useEffect(() => {
    if (method) {
      setState({ method });
      nextStep();
    }
  }, [method]);

  const onRadioChange = (method: Method) => {
    setMethod(method);
  };

  return (
    <div>
      <div className="flex py-5 md:py-20">
        <RadioGroup
          onChange={onRadioChange}
          className="flex items-center justify-center w-full gap-4 md:gap-7"
        >
          <RadioOption
            id="step-new-viab-conz-create"
            value={conzViab}
            icon={WaterDropOnSVG}
            titles={[
              `${t('measurements.viability')}  &`,
              t('measurements.concentration'),
            ]}
          />
          <RadioOption
            value={conz}
            icon={WaterDropOffSVG}
            titles={[t('measurements.only'), t('measurements.concentration')]}
          />
        </RadioGroup>
      </div>
      <div className="flex justify-center">
        {!errorMessage && isLoading ? (
          <SpinningIcon color={`fill-${theme.current}-primary`} />
        ) : null}
        <ErrorAlert message={errorMessage} close={() => setErrorMessage('')} />
      </div>
    </div>
  );
};

const RadioOption = ({
  id,
  icon,
  value,
  titles,
}: {
  id?: string;
  value: any;
  icon: string;
  titles: string[];
}) => {
  const theme: any = useTheme();
  return (
    <div id={id}>
      <RadioGroup.Option value={value}>
        {({ checked }) => (
          <div
            className={`flex flex-col items-center border-[3px] rounded-[40px] gap-2 p-5 md:p-10 md:px-24 cursor-pointer ${
              checked ? `bg-white border-${theme.current}-primary` : 'bg-gray'
            }`}
          >
            <div className="flex flex-col items-center">
              <img src={icon} alt="method" className="mb-2" />
              <span className="font-bold md:text-[1.75rem]">{titles[0]}</span>
              <span className="font-bold md:text-[1.75rem]">{titles[1]}</span>
            </div>
          </div>
        )}
      </RadioGroup.Option>
    </div>
  );
};

const getAnalysisMethods = (signedInMethod?: SignedInMethod) => {
  const _default = { conzViab: null, conz: null };

  const methods = {
    [SignedInMethod.Ethanol]: {
      conzViab: Method.BioEthConzViab,
      conz: Method.BioEthConz,
    },
    [SignedInMethod.Wine]: { conzViab: Method.WineConzViab, conz: Method.WineConz },
    [SignedInMethod.Beer]: { conzViab: Method.Yeast2ConzViab, conz: Method.Yeast2Conz },
  };

  return signedInMethod ? methods[signedInMethod] : _default;
};
